"use client";

/**
 * The listing page's one call to action on phones, floating above the
 * tab bar. Until 2026-10-14 it sent people to the analyzer ("Tam AI
 * analizi": 11 taps in 400 tracked sessions); it is now the reason to
 * come back: watch this listing for a price change. The channel follows
 * the device: an account, this browser's push, or, where push is
 * impossible (iOS Safari tabs, the largest group on these pages, hold
 * no push subscription at all), the Telegram bot's deep link. On a
 * first visit a line above the button says what the page holds below.
 * Desktop keeps the in-card buttons.
 */
import { useEffect, useState } from "react";
import { Heart, Send } from "lucide-react";
import MobileBottomChromeOffset from "@/components/MobileBottomChromeOffset";
import { useAuth } from "@/lib/auth-context";
import { returningDevice } from "@/lib/first-visit";
import { isPushEligible } from "@/lib/push";
import { listingSlug } from "@/lib/seo/paths";
import { telegramBotHref, telegramWatchHref } from "@/lib/telegram";
import { trackEvent } from "@/lib/tracking";
import { useWatchlist } from "@/lib/use-watchlist";

const TG_MARK_PREFIX = "watch:tg:";

export function telegramWatchMarked(listingId: string): boolean {
  try {
    return window.localStorage.getItem(TG_MARK_PREFIX + listingId) !== null;
  } catch {
    return false;
  }
}

/** Open the bot on `/start watch_<slug>` and remember the hand-off on
 *  this device (the chat holds the watch; this is all we can know). */
export function watchViaTelegram(listingId: string, src: string): boolean {
  const slug = listingSlug(listingId);
  if (!slug) return false;
  trackEvent("listing.watch", { id: listingId, on: true, src, via: "telegram" });
  window.open(telegramWatchHref(slug), "_blank", "noopener");
  try {
    window.localStorage.setItem(TG_MARK_PREFIX + listingId, String(Date.now()));
  } catch {
    /* private mode: the chat still holds the watch */
  }
  return true;
}

export interface StickyLabels {
  watch: string;
  watching: string;
  telegram: string;
  telegramOn: string;
  hint: string;
  jump: string;
}

export default function ListingStickyCta({
  listingId,
  hasVerdict,
  labels,
}: {
  listingId: string;
  hasVerdict: boolean;
  labels: StickyLabels;
}) {
  const { user } = useAuth();
  const { has, toggle } = useWatchlist();
  // Resolved after mount so the server and the first client render agree.
  const [push, setPush] = useState(true);
  const [tg, setTg] = useState(false);
  const [firstVisit, setFirstVisit] = useState(false);
  useEffect(() => {
    setPush(isPushEligible());
    setTg(telegramWatchMarked(listingId));
    setFirstVisit(!returningDevice());
  }, [listingId]);

  const watched = has(listingId);
  // Signed out and no push here: the bot is the channel, not a fallback.
  const viaTelegram = !user && !push;

  function onClick() {
    if (tg) {
      // The watch lives in the chat; the bot's message has the stop button.
      window.open(telegramBotHref(), "_blank", "noopener");
      return;
    }
    if (viaTelegram) {
      if (watchViaTelegram(listingId, "sticky")) setTg(true);
      return;
    }
    trackEvent("listing.watch", { id: listingId, on: !watched, src: "sticky" });
    void toggle(listingId);
  }

  const on = watched || tg;
  const label = tg ? labels.telegramOn : watched ? labels.watching : viaTelegram ? labels.telegram : labels.watch;
  return (
    <>
      <MobileBottomChromeOffset />
      <div className="fixed left-0 right-0 z-40 border-t border-gray-200 bg-white shadow-[0_-4px_12px_rgba(0,0,0,0.05)] lg:hidden sm:bottom-0 bottom-[calc(env(safe-area-inset-bottom)+4rem)]">
        <div className="mx-auto max-w-3xl px-4 py-3">
          {firstVisit && !on && (
            <p className="mb-2 flex items-center justify-between gap-3 text-xs text-gray-600">
              <span className="min-w-0 truncate">{labels.hint}</span>
              {hasVerdict && (
                <a
                  href="#ai-verdict"
                  data-track="listing.verdict.jump"
                  data-track-props={JSON.stringify({ id: listingId, src: "sticky" })}
                  className="shrink-0 font-semibold text-gray-900 underline underline-offset-2"
                >
                  {labels.jump}
                </a>
              )}
            </p>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClick}
              aria-pressed={on}
              className={`inline-flex min-w-0 flex-1 items-center justify-center gap-2 rounded-xl px-5 py-3.5 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 ${
                on
                  ? "border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
                  : "bg-indigo-600 text-white hover:bg-indigo-700"
              }`}
            >
              {viaTelegram && !on ? (
                <Send className="h-4 w-4" aria-hidden />
              ) : (
                <Heart className={`h-4 w-4 ${on ? "fill-rose-500 text-rose-500" : ""}`} aria-hidden />
              )}
              <span className="truncate">{label}</span>
            </button>
            {!user && push && !on && (
              <button
                type="button"
                onClick={() => { if (watchViaTelegram(listingId, "sticky")) setTg(true); }}
                className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3.5 py-3.5 text-sm font-semibold text-gray-900 hover:border-gray-300"
                aria-label={labels.telegram}
              >
                <Send className="h-4 w-4 text-gray-500" aria-hidden />
                <span className="hidden sm:inline">{labels.telegram}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
